import { useEffect, useState, useRef } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * TitleOverrideProvider — Session B.6 P3 (Feb 23, 2026)
 *
 * Runtime consumer of /title-overrides.json produced by
 * POST /api/admin/seo-audit/title-overrides/bulk-apply.
 *
 * Uses direct DOM manipulation (document.title + meta[name=description])
 * because Helmet's client-side updates are asynchronous — page-specific
 * Helmets can overwrite an override applied via a parent Helmet. Direct DOM
 * mutation is deterministic: our override runs LAST via a MutationObserver
 * that re-applies the override whenever another script changes <title>.
 */
const TitleOverrideProvider = () => {
  const [overrides, setOverrides] = useState({});
  const location = useLocation();
  const observerRef = useRef(null);

  useEffect(() => {
    fetch('/title-overrides.json', { cache: 'no-cache' })
      .then((r) => (r.ok ? r.json() : {}))
      .then(setOverrides)
      .catch(() => setOverrides({}));
  }, []);

  useEffect(() => {
    // Stop previous observer
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }

    const path = location.pathname;
    const override = overrides[path] || overrides[path.replace(/\/$/, '')];
    if (!override || !override.title) return undefined;

    const applyOverride = () => {
      if (document.title !== override.title) {
        document.title = override.title;
      }
      if (override.description) {
        let metaDesc = document.querySelector('meta[name="description"]');
        if (!metaDesc) {
          metaDesc = document.createElement('meta');
          metaDesc.setAttribute('name', 'description');
          // data-rh lets react-helmet-async adopt and replace this tag on its
          // own (async) flush instead of appending a second description —
          // the cause of the duplicate meta description seen on /services.
          metaDesc.setAttribute('data-rh', 'true');
          document.head.appendChild(metaDesc);
        }
        if (metaDesc.getAttribute('content') !== override.description) {
          metaDesc.setAttribute('content', override.description);
        }
        // Defensive: if a race left more than one description, keep the first.
        const descs = document.querySelectorAll('meta[name="description"]');
        for (let i = 1; i < descs.length; i += 1) descs[i].remove();
        // Also update og:title / og:description
        const setOG = (property, value) => {
          let el = document.querySelector(`meta[property="${property}"]`);
          if (!el) {
            el = document.createElement('meta');
            el.setAttribute('property', property);
            document.head.appendChild(el);
          }
          if (el.getAttribute('content') !== value) el.setAttribute('content', value);
        };
        setOG('og:title', override.title);
        setOG('og:description', override.description);
      }
    };

    // Apply after next tick to let page's own Helmet fire first
    const t1 = setTimeout(applyOverride, 300);
    const t2 = setTimeout(applyOverride, 1200);
    const t3 = setTimeout(applyOverride, 3000);

    // Observe <title> mutations — re-apply if page-level Helmet overwrites
    const titleEl = document.querySelector('title');
    if (titleEl && typeof MutationObserver !== 'undefined') {
      const observer = new MutationObserver(() => applyOverride());
      observer.observe(titleEl, { childList: true, characterData: true, subtree: true });
      observerRef.current = observer;
    }

    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
    };
  }, [location.pathname, overrides]);

  return null;
};

export default TitleOverrideProvider;
